import { type FormEvent, type ReactElement, type ReactNode, useState } from 'react';
import { useFieldErrors } from '../../shared/hooks/useFieldErrors.js';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import { Button, ErrorAlert, Field, TextInput } from '../../shared/ui/index.js';

export interface AuthFormProps {
  readonly title: string;
  readonly submitLabel: string;
  readonly passwordHint?: string;
  readonly footer: ReactNode;
  onSubmit(credentials: { username: string; password: string }): Promise<void>;
}

/**
 * Sign-in and sign-up differ by their copy and their endpoint, not by their
 * shape, so they share one form rather than two near-identical ones.
 */
export function AuthForm({
  title,
  submitLabel,
  passwordHint,
  footer,
  onSubmit,
}: AuthFormProps): ReactElement {
  const t = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);
  const fieldError = useFieldErrors(error);

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit({ username, password });
    } catch (caught) {
      setError(caught);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-layout">
      <div className="auth-card">
        <form className="stack" onSubmit={handleSubmit} noValidate>
          <div className="stack stack--tight">
            <h1>{title}</h1>
            <p className="page-header__subtitle">{t.auth.tagline}</p>
          </div>

          <ErrorAlert error={error} />

          <Field label={t.auth.usernameLabel} error={fieldError('username')}>
            {(props) => (
              <TextInput
                {...props}
                name="username"
                autoComplete="username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                required
              />
            )}
          </Field>

          <Field label={t.auth.passwordLabel} hint={passwordHint} error={fieldError('password')}>
            {(props) => (
              <TextInput
                {...props}
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            )}
          </Field>

          <Button type="submit" loading={submitting}>
            {submitLabel}
          </Button>

          <p className="field__hint">{footer}</p>
        </form>
      </div>
    </div>
  );
}
